import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { ProcessState } from '../types/processes/processTypes';
import { useNotification } from './NotificationsContext';
import { useServerNotifications } from './ServerNotificationsContext';
import { useActivitySession } from '../customHooks/useActivitySession';
import { getProcesses } from '../services/processes/processesServices';
import { FeedbackMessages } from '../utils/commonVariables/messages';
import {
    PROCESS_APPEARANCE_TIMEOUT_MS,
    PROCESS_POLLING_INTERVAL_MS,
    PROCESS_RESULTS_LIMIT,
} from '../utils/processes/processesUtils';

interface ProcessTrackingState {
    processes: ProcessState[];
    pending: { knownIds: Set<string>; deadline: number }[];
    matchedIds: Set<string>;
}
type ProcessTrackingAction =
    | { type: 'loaded'; processes: ProcessState[] }
    | { type: 'launched'; deadline: number }
    | { type: 'expired'; now: number }
    | { type: 'reset' };

const emptyTrackingState = (): ProcessTrackingState => ({
    processes: [],
    pending: [],
    matchedIds: new Set(),
});
const updateTracking = (
    state: ProcessTrackingState,
    action: ProcessTrackingAction
): ProcessTrackingState => {
    switch (action.type) {
        case 'reset':
            return emptyTrackingState();
        case 'launched':
            return {
                ...state,
                pending: [
                    ...state.pending,
                    {
                        knownIds: new Set(
                            state.processes.map(process => process.id)
                        ),
                        deadline: action.deadline,
                    },
                ],
            };
        case 'expired':
            return {
                ...state,
                pending: state.pending.filter(
                    item => item.deadline > action.now
                ),
            };
        case 'loaded': {
            const matchedIds = new Set(state.matchedIds);
            const pending = state.pending.filter(waiting => {
                const discovered = action.processes.find(
                    process =>
                        !waiting.knownIds.has(process.id) &&
                        !matchedIds.has(process.id)
                );
                if (discovered) {
                    matchedIds.add(discovered.id);
                }
                return !discovered;
            });
            return {
                processes: action.processes,
                pending,
                matchedIds: pending.length > 0 ? matchedIds : new Set(),
            };
        }
    }
};

interface ProcessesContextValue {
    processes: ProcessState[];
    hasActiveProcesses: boolean;
    loadProcesses: () => Promise<void>;
    isPollingEnabled: boolean;
    startPolling: () => void;
    totalActiveProcesses: number;
    isWaitingForProcess: boolean;
    isLoading: boolean;
}
const ProcessesContext = createContext<ProcessesContextValue | undefined>(
    undefined
);
interface ProcessScheduler {
    inFlight: boolean;
    refreshRequested: boolean;
    timer?: ReturnType<typeof setTimeout>;
}

/**
 * Tracks active processes and pending launches with one serialized refresh loop.
 * @param props.children Context consumers.
 * @returns The processes provider.
 */
export const ProcessesProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const session = useActivitySession();
    const { setErrorMessage } = useNotification();
    const { subscribeToRefresh, isConnected } = useServerNotifications();
    const [state, dispatch] = useReducer(
        updateTracking,
        undefined,
        emptyTrackingState
    );
    const [isLoading, setIsLoading] = useState(false);
    const activeSessionRef = useRef<typeof session | null>(session);
    activeSessionRef.current = session;
    const scheduler = useMemo<ProcessScheduler>(
        () => ({
            inFlight: false,
            refreshRequested: false,
        }),
        [session]
    );
    const isCurrentSession = useCallback(
        () =>
            session.isAuthenticated &&
            session.isCurrent() &&
            activeSessionRef.current === session,
        [session]
    );

    const loadProcesses = useCallback(async () => {
        if (!isCurrentSession()) {
            return;
        }
        clearTimeout(scheduler.timer);
        if (scheduler.inFlight) {
            scheduler.refreshRequested = true;
            return;
        }
        scheduler.inFlight = true;
        setIsLoading(true);
        try {
            do {
                scheduler.refreshRequested = false;
                try {
                    const processes = await getProcesses();
                    if (isCurrentSession()) {
                        dispatch({ type: 'loaded', processes });
                    }
                } catch {
                    if (isCurrentSession()) {
                        setErrorMessage(
                            FeedbackMessages.ERROR_LOADING_PROCESSES
                        );
                    }
                }
            } while (scheduler.refreshRequested && isCurrentSession());
        } finally {
            scheduler.inFlight = false;
            if (isCurrentSession()) {
                setIsLoading(false);
            }
        }
    }, [scheduler, isCurrentSession, setErrorMessage]);

    useEffect(() => {
        activeSessionRef.current = session;
        dispatch({ type: 'reset' });
        setIsLoading(false);
        void loadProcesses();
        return () => {
            activeSessionRef.current = null;
            clearTimeout(scheduler.timer);
        };
    }, [session, scheduler, loadProcesses]);

    useEffect(() => {
        if (session.isAuthenticated) {
            return subscribeToRefresh(() => void loadProcesses());
        }
    }, [session, subscribeToRefresh, loadProcesses]);

    const visibleProcesses = session.isAuthenticated
        ? state.processes.filter(process => process.active)
        : [];
    const hasActiveProcesses = visibleProcesses.length > 0;
    const isWaitingForProcess =
        session.isAuthenticated && state.pending.length > 0;
    const isPollingEnabled =
        isWaitingForProcess || (hasActiveProcesses && !isConnected);

    useEffect(() => {
        if (isPollingEnabled && !isLoading) {
            scheduler.timer = setTimeout(
                () => void loadProcesses(),
                PROCESS_POLLING_INTERVAL_MS
            );
            return () => clearTimeout(scheduler.timer);
        }
    }, [isPollingEnabled, isLoading, scheduler, loadProcesses]);

    useEffect(() => {
        if (isWaitingForProcess) {
            const deadline = Math.min(
                ...state.pending.map(item => item.deadline)
            );
            const timer = setTimeout(() => {
                dispatch({ type: 'expired', now: Date.now() });
                setErrorMessage(FeedbackMessages.ERROR_PROCESS_NOT_VISIBLE);
            }, Math.max(0, deadline - Date.now()));
            return () => clearTimeout(timer);
        }
    }, [state.pending, isWaitingForProcess, setErrorMessage]);

    const startPolling = useCallback(() => {
        if (isCurrentSession()) {
            dispatch({
                type: 'launched',
                deadline: Date.now() + PROCESS_APPEARANCE_TIMEOUT_MS,
            });
        }
    }, [isCurrentSession]);

    return (
        <ProcessesContext.Provider
            value={{
                processes: visibleProcesses.slice(0, PROCESS_RESULTS_LIMIT),
                hasActiveProcesses,
                loadProcesses,
                isPollingEnabled,
                startPolling,
                totalActiveProcesses: visibleProcesses.length,
                isWaitingForProcess,
                isLoading,
            }}
        >
            {children}
        </ProcessesContext.Provider>
    );
};

/**
 * Reads active processes and their refresh/launch actions.
 * @returns The shared processes context.
 */
export const useProcesses = () => {
    const context = useContext(ProcessesContext);
    if (!context) {
        throw new Error('useProcesses must be used within ProcessesProvider');
    }
    return context;
};
